import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import GoogleButton from "@/components/GoogleButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";

export default function Register() {
  const { t } = useI18n();
  const { loginWith } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", password: "", confirm_password: "",
    referral_code: sp.get("ref") || "",
  });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      loginWith(data.token, data.user);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={t("create_account")} subtitle={t("tagline")}
      footer={<>{t("have_account")} <Link to="/login" className="text-orange-400 font-semibold" data-testid="go-login-link">{t("sign_in")}</Link></>}>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>{t("name")}</Label><Input data-testid="register-name-input" value={form.name} onChange={set("name")} required className="mt-1" /></div>
        <div><Label>{t("email")}</Label><Input data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} required className="mt-1" /></div>
        <div><Label>{t("mobile")}</Label><Input data-testid="register-mobile-input" value={form.mobile} onChange={set("mobile")} required className="mt-1" placeholder="+92 3xx xxxxxxx" /></div>
        <div>
          <Label>{t("password")}</Label>
          <div className="relative mt-1">
            <Input data-testid="register-password-input" type={show ? "text" : "password"} value={form.password} onChange={set("password")} required />
            <button type="button" onClick={() => setShow(!show)} className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          </div>
        </div>
        <div><Label>{t("confirm_password")}</Label><Input data-testid="register-confirm-input" type={show ? "text" : "password"} value={form.confirm_password} onChange={set("confirm_password")} required className="mt-1" /></div>
        <div><Label>{t("referral_code")}</Label><Input data-testid="register-referral-input" value={form.referral_code} onChange={set("referral_code")} className="mt-1" placeholder="optional" /></div>
        <Button type="submit" disabled={busy} data-testid="register-submit-button" className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold py-6 mt-2">
          {busy ? "..." : t("sign_up")}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5 text-gray-500 text-xs"><div className="flex-1 h-px bg-white/10" />{t("or")}<div className="flex-1 h-px bg-white/10" /></div>
      <GoogleButton />
    </AuthShell>
  );
}
